
// Tabela de consumo médio (km/l) baseada em dados reais de mercado
// Organizada por tipo de veículo e faixa de velocidade média

interface ConsumptionProfile {
  city: number;    // consumo urbano (até 40 km/h média)
  mixed: number;   // consumo misto (40-60 km/h média)
  highway: number; // consumo rodoviário (acima de 60 km/h média)
}

// Consumo base por tipo de veículo (km/l)
const BASE_CONSUMPTION: Record<string, ConsumptionProfile> = {
  moto: {
    city: 35,
    mixed: 40,
    highway: 32,
  },
  carro: {
    city: 10,
    mixed: 13,
    highway: 15,
  },
};

// Fator de ajuste por ano do veículo
// Veículos mais novos tendem a ser mais eficientes
function getYearFactor(yearStr: string): number {
  const yearMatch = yearStr.match(/(\d{4})/);
  if (!yearMatch) return 1.0;

  const year = parseInt(yearMatch[1], 10);
  const currentYear = new Date().getFullYear();
  const age = currentYear - year;

  if (age <= 2) return 1.05;   // veículo novo, +5%
  if (age <= 5) return 1.0;    // referência
  if (age <= 10) return 0.93;  // -7%
  if (age <= 15) return 0.85;  // -15%
  return 0.78;                  // -22% para veículos muito antigos
}

// Determina a faixa de velocidade
function getSpeedCategory(avgSpeedKmh: number): keyof ConsumptionProfile {
  if (avgSpeedKmh <= 40) return 'city';
  if (avgSpeedKmh <= 60) return 'mixed';
  return 'highway';
}

export interface FuelEstimate {
  avgConsumption: number;  // km/l estimado
  avgSpeed: number;        // km/h média
  speedCategory: string;   // urbano, misto, rodoviário
  totalFuelUsed: number;   // litros estimados gastos
}

/**
 * Calcula o consumo médio estimado do veículo com base em:
 * - Tipo do veículo (moto/carro)
 * - Ano do veículo
 * - Velocidade média percorrida nas rotas
 * - Distância total percorrida
 */
export function estimateFuelConsumption(
  vehicleType: string,
  vehicleModel: string,
  totalDistanceKm: number,
  totalDurationSeconds: number,
): FuelEstimate {
  // Calcular velocidade média (km/h)
  const totalHours = totalDurationSeconds / 3600;
  const avgSpeed = totalHours > 0 ? totalDistanceKm / totalHours : 0;

  // Tipo base
  const type = vehicleType === 'moto' ? 'moto' : 'carro';
  const baseProfile = BASE_CONSUMPTION[type];

  // Categoria de velocidade
  const speedCat = getSpeedCategory(avgSpeed);
  let baseConsumption = baseProfile[speedCat];

  // Extrair ano do modelo (formato: "Marca Modelo (Ano)")
  const yearMatch = vehicleModel.match(/\(([^)]+)\)/);
  const yearStr = yearMatch ? yearMatch[1] : '';
  const yearFactor = getYearFactor(yearStr);

  // Aplicar fator do ano
  const adjustedConsumption = baseConsumption * yearFactor;

  // Calcular litros gastos
  const totalFuelUsed = totalDistanceKm > 0 ? totalDistanceKm / adjustedConsumption : 0;

  // Traduzir categoria
  const categoryLabels: Record<string, string> = {
    city: 'Urbano',
    mixed: 'Misto',
    highway: 'Rodoviário',
  };

  return {
    avgConsumption: Math.round(adjustedConsumption * 10) / 10,
    avgSpeed: Math.round(avgSpeed * 10) / 10,
    speedCategory: categoryLabels[speedCat] || 'Misto',
    totalFuelUsed: Math.round(totalFuelUsed * 100) / 100,
  };
}
